/**
 * Own cookie-less analytics (docs/superpowers/specs/2026-09-30-analytics-design.md): the beacon contract and the
 * pure normalisers shared by the SPA and the server — templates instead of ids, domains instead of URLs,
 * coarse device classes instead of user agents.
 */
import { z } from 'zod';
import { GAME_IDS, LOCALES, type GameId, type Locale } from './common';
import { gamePathOf } from './catalog';
import { SHARE_CHANNELS } from './share';

export const AN_DEFAULTS = {
  rawDays: 90,
  playerDays: 390,
  sessionGapMin: 30,
  vhPerMin: 30,
  routePerMin: 120,
  dimTop: 100,
  betLagMs: 120_000,
  chunkMin: 60,
  chunksPerTick: 24,
  tickMs: 60_000,
  webRefreshMs: 300_000,
  flushMs: 1_000,
  batchMax: 500,
  purgeBatch: 5_000,
  purgeBatches: 20,
  maxRangeDays: 366,
  onlineMin: 5,
} as const;
/** tunables of the module (config.analytics.opts; tests shrink them) */
export type AnOptions = { -readonly [K in keyof typeof AN_DEFAULTS]: number };

/** screen width bucket + touch flag read by JS (spec §2: can be switched off if the lawyer says so) */
export const AN_CLIENT_TRAITS = true;

export const AN_DIMS = ['page', 'src', 'ref', 'camp', 'cc', 'lang', 'dev', 'os', 'br', 'sw', 'theme', 'auth'] as const;
export type AnDim = (typeof AN_DIMS)[number];
export const AN_OTHER = '(other)';

/**
 * Roles left out of all analytics (owner decision 2026-09-30): their beacons are dropped and their bets,
 * rounds, sessions, signups and cohorts are not counted in any game metric. The role is the current one
 * (`User.role` at recomputation time), not historical.
 */
export const AN_EXCLUDED_ROLES = ['admin'] as const;

/* ---------------- paths ---------------- */

const SYSTEM_PATHS = ['/profile', '/fair', '/responsible', '/tournaments', '/friends', '/messages', '/admin', '/signin', '/signup', '/verify', '/privacy', '/cookies', '/cooke'];
const PARAM_PATHS: Array<[RegExp, string]> = [
  [/^\/a\/[^/]{1,64}$/, '/a/:id'],
  [/^\/i\/[^/]{1,64}$/, '/i/:code'],
  [/^\/tournaments\/t\/[^/]{1,64}$/, '/tournaments/t/:id'],
  // an open conversation: the partner's publicId never leaves the device
  [/^\/messages\/[^/]{1,64}$/, '/messages/:id'],
];

/** every template the server accepts (lobby, games, system pages, share pages, 404) */
export const AN_PATHS: readonly string[] = ['/', ...GAME_IDS.map(gamePathOf), ...SYSTEM_PATHS, ...PARAM_PATHS.map(([, t]) => t), '/404'];
const EXACT = new Set(AN_PATHS);

/** "/ru/a/X7K…" → { lang: 'ru', path: '/a/:id' }; short links without a language → lang ''. */
export function normalizeAnPath(pathname: string): { lang: Locale | ''; path: string } | null {
  if (typeof pathname !== 'string' || pathname.length > 200 || !pathname.startsWith('/')) return null;
  const clean = pathname.replace(/\/+$/, '') || '/';
  const seg = clean.split('/');
  const first = seg[1] ?? '';
  let lang: Locale | '' = '';
  let rest = clean;
  if ((LOCALES as readonly string[]).includes(first)) {
    lang = first as Locale;
    rest = '/' + seg.slice(2).join('/');
    if (rest === '/') return { lang, path: '/' };
  } else if (!/^\/[ai]\//.test(clean)) {
    return null;
  }
  if (EXACT.has(rest) && !rest.includes(':')) return { lang, path: rest };
  for (const [re, t] of PARAM_PATHS) if (re.test(rest)) return { lang, path: t };
  return lang ? { lang, path: '/404' } : null;
}

/* ---------------- referrer and source ---------------- */

const DOMAIN_RE = /^[a-z0-9.-]{1,64}$/;
const ALIASES: Record<string, string> = {
  't.co': 'x.com',
  'twitter.com': 'x.com',
  'l.facebook.com': 'facebook.com',
  'lm.facebook.com': 'facebook.com',
  'm.facebook.com': 'facebook.com',
  't.me': 'telegram',
  'web.telegram.org': 'telegram',
  'away.vk.com': 'vk.com',
  'm.vk.com': 'vk.com',
  'l.instagram.com': 'instagram.com',
  'out.reddit.com': 'reddit.com',
  'com.google.android.gm': 'gmail',
  'mail.google.com': 'gmail',
};

/** Only the host of the referrer (never the path or query); own hosts → '' (internal navigation). */
export function refDomain(referrer: string, ownHosts: readonly string[]): string {
  if (!referrer) return '';
  const app = /^android-app:\/\/([a-z0-9._]{1,64})/i.exec(referrer);
  if (app) {
    const id = app[1]!.toLowerCase();
    return ALIASES[id] ?? `app:${id}`;
  }
  let u: URL;
  try {
    u = new URL(referrer);
  } catch {
    return '';
  }
  if (u.protocol !== 'http:' && u.protocol !== 'https:') return '';
  if (ownHosts.includes(u.host.toLowerCase())) return '';
  const host = u.hostname.toLowerCase().replace(/^www\./, '');
  if (!DOMAIN_RE.test(host)) return '';
  return ALIASES[host] ?? host;
}

const TOKEN_RE = /^[a-z0-9._-]{1,32}$/;
export function cleanToken(s: string | undefined, max = 32): string {
  const t = (s ?? '').trim().toLowerCase().slice(0, max);
  return t && new RegExp(`^[a-z0-9._-]{1,${max}}$`).test(t) ? t : '';
}

/** share channel > utm_source > referrer domain > direct */
export function resolveSource(x: { c?: string; us?: string; ref: string }): string {
  if (x.c && (SHARE_CHANNELS as readonly string[]).includes(x.c)) return `share:${x.c}`;
  const us = cleanToken(x.us);
  if (us && TOKEN_RE.test(us)) return `utm:${us}`;
  return x.ref ? `ref:${x.ref}` : 'direct';
}

/* ---------------- user agent ---------------- */

export type AnOs = 'ios' | 'android' | 'windows' | 'macos' | 'linux' | 'chromeos' | 'harmony' | 'other';
export type AnBrowser = 'wechat' | 'qq' | 'uc' | 'samsung' | 'yandex' | 'edge' | 'opera' | 'firefox' | 'chrome' | 'safari' | 'other';

export const BOT_UA_RE =
  /bot\b|bot\/|crawl|spider|slurp|headless|lighthouse|preview|facebookexternalhit|whatsapp|telegrambot|bingpreview|python|curl\/|wget|go-http|java\/|okhttp|axios|node-fetch|undici|puppeteer|playwright|selenium|phantom|scrapy|httpclient|monitor|uptime/i;

/** Coarse classes only; `touch` = navigator.maxTouchPoints > 0 (iPadOS reports a Mac user agent). */
export function parseUa(ua: string, touch: boolean): { dev: 'm' | 't' | 'd'; os: AnOs; br: AnBrowser } {
  const macTouch = /Macintosh/.test(ua) && touch;
  const os: AnOs = /HarmonyOS|OpenHarmony/.test(ua)
    ? 'harmony'
    : /iPhone|iPad|iPod/.test(ua) || macTouch
      ? 'ios'
      : /Android/.test(ua)
        ? 'android'
        : /Windows/.test(ua)
          ? 'windows'
          : /CrOS/.test(ua)
            ? 'chromeos'
            : /Macintosh|Mac OS X/.test(ua)
              ? 'macos'
              : /Linux|X11/.test(ua)
                ? 'linux'
                : 'other';
  const dev: 'm' | 't' | 'd' =
    /iPad|Tablet/.test(ua) || macTouch || (/Android/.test(ua) && !/Mobile/.test(ua)) ? 't' : /Mobi|iPhone|iPod|Android/.test(ua) ? 'm' : 'd';
  const br: AnBrowser = /MicroMessenger/.test(ua)
    ? 'wechat'
    : /\bQQ\/|MQQBrowser|QQBrowser/.test(ua)
      ? 'qq'
      : /UCBrowser|UCWEB/.test(ua)
        ? 'uc'
        : /SamsungBrowser/.test(ua)
          ? 'samsung'
          : /YaBrowser/.test(ua)
            ? 'yandex'
            : /Edg\//.test(ua)
              ? 'edge'
              : /OPR\/|Opera/.test(ua)
                ? 'opera'
                : /Firefox\/|FxiOS/.test(ua)
                  ? 'firefox'
                  : /Chrome\/|CriOS/.test(ua)
                    ? 'chrome'
                    : /Safari\//.test(ua)
                      ? 'safari'
                      : 'other';
  return { dev, os, br };
}

/** Breakpoints of common analytics tools: phone / tablet / laptop / desktop. */
export function screenBucket(w: number | undefined): 's' | 'm' | 'l' | 'x' | '' {
  if (w === undefined || !Number.isFinite(w) || w <= 0) return '';
  return w < 576 ? 's' : w < 992 ? 'm' : w < 1440 ? 'l' : 'x';
}

/* ---------------- days ---------------- */

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
export const utcDay = (d: Date): string => d.toISOString().slice(0, 10);
export const addDays = (day: string, n: number): string => utcDay(new Date(Date.parse(`${day}T00:00:00Z`) + n * 86_400_000));
/** inclusive number of days from `from` to `to` */
export const daysBetween = (from: string, to: string): number => Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000) + 1;

/* ---------------- schemas ---------------- */

const opt = (max: number) => z.string().max(max).optional();
export const zAnBeacon = z
  .object({
    p: z.string().min(1).max(200),
    r: opt(500),
    us: opt(64),
    uc: opt(64),
    c: opt(8),
    th: z.enum(['d', 'l']),
    w: z.number().int().min(0).max(20_000).optional(),
    t: z.union([z.literal(0), z.literal(1)]).optional(),
  })
  .strict();
export type AnBeacon = z.infer<typeof zAnBeacon>;

const zDay = z.string().regex(DAY_RE);
export function zAnRange(today: string) {
  return z
    .object({ from: zDay, to: zDay, compare: z.enum(['0', '1', 'true', 'false']).optional() })
    .refine((r) => r.from <= r.to && r.to <= today, { message: 'range' })
    .refine((r) => daysBetween(r.from, r.to) <= AN_DEFAULTS.maxRangeDays, { message: 'too long' })
    .transform((r) => ({ from: r.from, to: r.to, compare: r.compare === undefined || r.compare === '1' || r.compare === 'true' }));
}
export type AnRange = z.output<ReturnType<typeof zAnRange>>;

/* ---------------- report DTO ---------------- */
/* Every number below excludes users with a role from AN_EXCLUDED_ROLES (visits, signups, players, bets, cohorts). */

export interface AnKpis {
  visitors: number;
  pageviews: number;
  sessions: number;
  /** single-page sessions / sessions */
  bounceRate: number | null;
  avgSessionSec: number | null;
  signups: number;
  /**
   * visitors (daily hashes) with a sign-up from the same browser on the same UTC day — a subset of `visitors`
   * (several accounts from one browser count once)
   */
  convertedVisitors: number;
  /** sign-ups with no recorded visit of their browser that day: API / scripts, DNT/GPC, a changed network, no beacon */
  signupsNoVisit: number;
  /** convertedVisitors / visitors, 0..1; null without visitors */
  conversion: number | null;
  players: number;
  newPlayers: number;
  /** average daily active players over the period */
  dau: number;
  /** distinct players in the 7 / 30 days ending at `to` */
  wau: number;
  mau: number;
  rounds: number;
  bets: number;
  staked: number;
  avgPlayMin: number | null;
}
export interface AnDayPoint {
  day: string;
  visitors: number;
  pageviews: number;
  signups: number;
  players: number;
  rounds: number;
  staked: number;
}
export interface AnTopRow {
  key: string;
  visitors: number;
  pageviews: number;
}
export interface AnGameRow {
  game: GameId;
  players: number;
  newPlayers: number;
  rounds: number;
  bets: number;
  staked: number;
  avgSessionMin: number | null;
}
export interface AnRet {
  returned: number;
  /** registered users whose day D+N has already passed */
  eligible: number;
}
export interface AnFunnel {
  visitors: number;
  /** visitors who signed up (see AnKpis.convertedVisitors) — the visitors → sign-up step, ≤ visitors */
  convertedVisitors: number;
  /** accounts registered in the period */
  signups: number;
  /** of them without a recorded visit (see AnKpis.signupsNoVisit) */
  signupsNoVisit: number;
  /** of the users registered in the period: made at least one bet */
  firstBet: number;
  d1: AnRet;
  d7: AnRet;
  d30: AnRet;
}
export const AN_LEVEL_BUCKETS = ['1-9', '10-19', '20-29', '30-49', '50+'] as const;
export interface AnPlayersMix {
  newPlayers: number;
  returning: number;
  levels: Array<{ bucket: (typeof AN_LEVEL_BUCKETS)[number]; players: number }>;
}
export interface AnReport {
  from: string;
  to: string;
  prevFrom: string;
  prevTo: string;
  /** ISO time of the freshest complete recomputation of the last day */
  updatedAt: string | null;
  online: number;
  kpis: AnKpis;
  prev: AnKpis | null;
  series: AnDayPoint[];
  prevSeries: AnDayPoint[] | null;
  tops: Record<AnDim, AnTopRow[]>;
  signupChannels: AnTopRow[];
  games: AnGameRow[];
  funnel: AnFunnel;
  players: AnPlayersMix;
}
export interface AnCohortRow {
  day: string;
  size: number;
  /** returned share 0..1; null = day D+N not reached yet */
  d1: number | null;
  d7: number | null;
  d30: number | null;
}
